<template>
	<Modal
		:title="$I18n.trans('one.latest_tags')"
		:class="{'is-mobile': isMobileWebApp}"
		:full-screen="isMobileWebApp"
		:type="isMobileWebApp ? 'fullscreen' : 'fit-parent'"
		:blocking="isMobileWebApp"
		:open="true"
		:has-footer="false"
		@close="close"
		@cancel="close"
	>
		<template #body>
			<div class="modal-body">
				<div class="top">
					<p v-if="!isMobileWebApp">
						{{ $I18n.trans('one.edit_or_delete_tags') }}
					</p>
					<div class="search">
						<div class="input">
							<input
								v-model="searchQuery"
								type="text"
								:placeholder="$I18n.trans('one.search')"
							>
							<div class="icon">
								<Icon
									v-if="!searchQuery"
									:size="isMobileWebApp ? 'sm' : 'lg'"
									icon-name="search"
								/>
								<Icon
									v-else
									:size="isMobileWebApp ? 'sm' : 'lg'"
									icon-name="e-remove"
									@click="resetSearchQuery"
								/>
							</div>
						</div>
					</div>
				</div>

				<div class="tags">
					<div
						v-for="item in items"
						:key="item.id"
						class="tag"
					>
						<div class="tag-action">
							<span class="action-cell-icon">
								<Icon
									:icon-name="`tag-${item.name}`"
									color-class="mr-1"
									size="sm"
								/>
							</span>
							<LabelWithTooltip
								v-slot="{ label }"
								:label="transTagLabel(item.name, sportName)"
							>
								<div class="tag-name">
									{{ label }}
								</div>
							</LabelWithTooltip>
						</div>
						<div class="tag-player">
							<span v-if="item.player">
								{{ item.player.name }}
							</span>
						</div>
						<div class="tag-team">
							<span v-if="item.tag.team && item.tag.team.club">
								{{ item.tag.team.club.code }}
							</span>
						</div>
						<div
							v-if="!isMobileWebApp"
							class="tag-time"
						>
							{{ item.time }}
						</div>
						<div class="tag-options">
							<ContextMenu
								:key="`context_menu${item.id}`"
								:menu-items="rowContextMenuItems"
								:value="item"
							>
								<template #toggle>
									<span class="sc-white">
										<Icon
											icon-name="context-menu"
											size="md"
										/>
									</span>
								</template>
							</ContextMenu>
						</div>
					</div>
				</div>
			</div>
		</template>
	</Modal>
</template>

<script>
import Modal from '@components/modal/Modal';
import { mapGetters, mapActions } from 'vuex';
import ContextMenu from '@components/context-menu/ContextMenu.vue';
import { bus } from '@videotools/js/events';

const TIMING_TAG = {
	CLOCK_START: 'clock_start',
	CLOCK_STOP:  'clock_end',
	CLOCK_SET:   'clock_set',
};
const TIMING_TAGS = [
	TIMING_TAG.CLOCK_START,
	TIMING_TAG.CLOCK_STOP,
	TIMING_TAG.CLOCK_SET,
];

export default {
	components: {
		Modal,
		ContextMenu,
	},
	data() {
		return {
			isVertical:          false,
			searchQuery:         '',
			selectedPlayerId:    null,
			playerNumberMap:     {},
			rowContextMenuItems: [
				{
					id:      'edit',
					label:   this.transUcfirst('commons.edit'),
					icon:    'pen',
					action:  'edit',
					handler: this.handleEdit,
				},
				{
					id:      'delete',
					label: 	 this.transUcfirst('commons.delete'),
					icon:    'bin',
					action:  'delete',
					class:   'danger',
					handler: this.handleDelete,
				},
			],
		};
	},
	computed: {
		...mapGetters({
			isMobileWebApp:    'app/isMobileWebApp',
			eventParticipants: 'event/participants',
			modalData:         'scoreboardControl/assignPlayerModalData',
			tags:              'event/tags',
			sportName:         'event/sportName',
			canEditEventTag:   'permissions/canEditEventTag',
		}),
		labels() {
			return {
				noTagsAvailable: this.transUcfirst('commons.no_types_found', { type: 'tags' }),
			};
		},
		items() {
			const _items = [];

			for (const tag of this.tags) {
				if (!TIMING_TAGS.includes(tag.name)) {
					_items.push({
						id:                 tag.id,
						tag,
						name:               tag.name,
						time:               tag.formattedTimestamp,
						duration:           tag.formattedDuration,
						imageUrl:           tag.imageUrl,
						player:             tag.player,
						team:               tag.teamName,
						timestamp:          tag.timestamp,
						participantName:    tag.participantName,
						action:             tag.name,
						contextMenuEnabled: this.canEditEventTag(tag),
					});
				}
			}

			return _items
				.filter((item) => this.transTagLabel(item.name, this.sportName).toLowerCase().indexOf(this.searchQuery.toLowerCase()) !== -1 ||
					item?.player?.name?.toLowerCase()?.indexOf(this.searchQuery.toLowerCase()) >= 0 ||
					item?.participantName?.toLowerCase()?.indexOf(this.searchQuery.toLowerCase()) >= 0)
				.sort((a, b) => a.id < b.id ? 1 : -1);
		},
	},
	methods: {
		...mapActions({
			hideLatestTagsModal: 'scoreboardControl/hideLatestTagsModal',
			deleteEventTags:     'event/deleteEventTags',
			editEventTag:        'eventTagEdit/editEventTag',
		}),
		resetSearchQuery() {
			this.searchQuery = '';
		},
		async handleDelete(item) {
			try {
				await this.$confirmDelete({
					title: this.$I18n.trans('commons.type_delete', {
						type: this.$I18n.trans_choice('one.tag', 1),
					}),
					body: this.$I18n.trans('one.scoreboard.delete_tag', {
						tagName: this.transTagLabel(item.name, this.sportName),
					}),
				});
				await this.deleteEventTags([item]);
				bus.publish('custom-table:clear-selection');
			} catch (_) {}
		},
		handleEdit(item) {
			this.editEventTag(item.tag);
		},
		close() {
			this.hideLatestTagsModal();
		},
		submit() {
			this.close();
		},
	},
};
</script>

<style lang="scss" scoped>
	@import "@videotools/sass/fonts";

	.modal-body {
		display: flex;
		flex-direction: column;
		height: 100%;
	}

	.tags {
		overflow: auto;
		:last-child.player {
			border-bottom: 1px solid var(--color-grey-800);
		}
	}

	.tag {
		display: flex;
		align-items: center;
		height: 48px;
		width: 100%;
		border-top: 1px solid var(--color-grey-800);
		cursor: pointer;
		@include text-sm-2;

		&:hover {
			background-color: var(--color-grey-850);
		}

		.tag-action {
			width: 80px;
			display: flex;
			margin-left: 24px;
			margin-right: 36px;
		}

		.tag-player {
			flex-grow: 1;
			overflow: hidden;
			text-overflow: ellipsis;
			white-space: nowrap;
		}

		.tag-time {
			margin-left: 24px;
		}

		.tag-team {
			margin-left: 24px;
		}

		.tag-options {
			margin-left: 24px;
			margin-right: 24px;
		}
	}

	.top {
		padding: 0 24px 0 24px;
	}

	.action-cell-icon {
		display: flex;
		flex-direction: row;
		margin-right: 0.5rem;
	}

	.is-mobile {
		.tag {
			.tag-action {
				margin-left: 16px;
			}
			.options {
				margin-left: 16px;
			}
		}
		.search {
			margin-top: 24px;
			margin-bottom: 24px;
		}

		.search .input input {
			padding: 10px 48px 10px 16px;

			&::placeholder {
				font-size: 16px;
			}
		}

		.top {
			padding: 0 16px 0 16px;
		}

		::v-deep {
			.modal--header {
				border-bottom: 1px solid var(--color-grey-800);
			}
			.modal--title {
				margin-bottom: 0 !important;
			}
			.modal--body {
				padding: 0;
			}
		}
	}

	::v-deep {
		.modal--header {
			padding: 24px 24px 0 24px;
			border-bottom: none;
		}
		.modal--content {
			padding: 0 !important;
			&.desktop:not(.fit-parent) {
				max-height: 100%;
			}
		}

		.modal--body {
			padding: 0;
		}
	}

	input[type=radio] {
		color: var(--color-grey-300);
		-webkit-appearance: none;
		appearance: none;
		width: 16px;
		height: 16px;
		border: 1px solid currentColor;
		border-radius: 50%;
		display: flex;
		align-items: center;
		justify-content: center;

		&::before {
			content: "";
			width: 8px;
			height: 8px;
			border-radius: 50%;
			transform: scale(0);
			transition: 120ms transform ease-in-out;
			box-shadow: inset 1em 1em var(--form-control-color);
			background-color: var(--color-hype-yellow);
			display: flex;
		}
	}

	input[type=radio]:checked {
		color: var(--color-hype-yellow);
		&::before {
			transform: scale(1);
		}
	}

	.search {
		margin-top: 32px;
		margin-bottom: 32px;

		.input {
			position: relative;
			margin-left: 1px;
			margin-right: 1px;

			.icon {
				position: absolute;
				top: 12px;
				right: 16px;
				width: 24px;
				height: calc(100% - 24px);
			}

			input {
				width: 100%;
				background: var(--color-black);
				border-radius: 24px;
				padding: 14px 48px 14px 16px;
				line-height: normal;
				box-shadow: 0 0 0 1px var(--color-grey-850);

				&:focus {
					box-shadow: 0 0 0 1px var(--color-hype-yellow);
				}
			}
		}
	}
</style>
